import React from 'react';
interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  children: React.ReactNode;
  testId?: string;
  className?: string;
  /** If set, this variant is appended to the cart when the button is clicked. */
  bumpVariantId?: string;
  /** Override the default primary variant ID (AI Income Blueprint). */
  variantId?: string;
  /** If set, scrolls to this element ID instead of navigating to Shopify. */
  scrollTo?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}
/**
 * PRIMARY CTA BUTTON.
 *
 * - Fires Meta Pixel AddToCart on click.
 * - Navigates to Shopify /cart/<variantId>:1[,<bumpVariantId>:1] permalink.
 *   Shopify's /cart permalink supports multiple line items separated by commas,
 *   so if bumpVariantId is passed (from the offer-section order bump checkbox),
 *   both items get added to the cart in one click.
 *
 * SETUP:
 * 1. Create the main book product in Shopify, grab its variant ID, and replace
 *    REPLACE_VARIANT_ID below.
 * 2. Create the Mega Vault bump product in Shopify, grab its variant ID, and
 *    replace REPLACE_BUMP_VARIANT_ID below (if you want the bump wired).
 */
const DEFAULT_VARIANT_ID = '44874756980781';
export function Button({
  children,
  testId,
  className = '',
  bumpVariantId,
  variantId = DEFAULT_VARIANT_ID,
  scrollTo,
  onClick,
  ...props
}: ButtonProps) {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    // If scrollTo is set, scroll to that section instead of navigating
    if (scrollTo) {
      const el = document.getElementById(scrollTo);
      if (el) {
        el.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
      if (onClick) onClick(e);
      return;
    }
    // Fire Meta Pixel AddToCart
    if (typeof window !== 'undefined' && (window as any).fbq) {
      ;
      (window as any).fbq('track', 'AddToCart');
    }
    // Build Shopify /cart permalink. Supports multi-item via comma list.
    const items = bumpVariantId ? `${variantId}:1,${bumpVariantId}:1` : `${variantId}:1`;
    const url = `https://simplepersonalgrowth.store/cart/${items}`;
    // Let the consumer's onClick run first (it can e.preventDefault() to cancel).
    if (onClick) onClick(e);
    if (!e.defaultPrevented) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };
  return <button data-testid={testId} onClick={handleClick} className={`
        bg-brand text-black font-archivo uppercase tracking-tight
        px-8 py-4 rounded-[10px] text-lg sm:text-xl
        transition-all duration-200 ease-out
        hover:-translate-y-[1px] hover:shadow-glow
        active:translate-y-[1px] active:shadow-none
        ${className}
      `} {...props} data-id="element-184">
      {children}
    </button>;
}